import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const { t } = useTranslation();
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await signup(formData.email, formData.password, formData.name);
      }
      // Redirect back to booking if came from there, otherwise dashboard
      const from = location.state?.from || '/dashboard';
      navigate(from);
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24 bg-[#0A0A0A]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-8">
          <h2 className="text-3xl font-serif text-[#F5F2EB] text-center mb-8">
            {isLogin ? t('login') : t('signup')}
          </h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="text-sm text-[#A3A3A3] mb-1 block uppercase tracking-wider text-xs">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-0 top-3 w-5 h-5 text-[#C9A84C]" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-transparent border-b border-[#C9A84C]/30 px-8 py-3 text-[#F5F2EB] focus:ring-0 focus:border-[#C9A84C] focus:outline-none placeholder:text-[#A3A3A3] transition-colors"
                    placeholder="Enter your name"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-[#A3A3A3] mb-1 block uppercase tracking-wider text-xs">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-0 top-3 w-5 h-5 text-[#C9A84C]" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 px-8 py-3 text-[#F5F2EB] focus:ring-0 focus:border-[#C9A84C] focus:outline-none placeholder:text-[#A3A3A3] transition-colors"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A3A3A3] mb-1 block uppercase tracking-wider text-xs">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-0 top-3 w-5 h-5 text-[#C9A84C]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 px-8 py-3 text-[#F5F2EB] focus:ring-0 focus:border-[#C9A84C] focus:outline-none placeholder:text-[#A3A3A3] transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-3 text-[#C9A84C] hover:text-[#E5C870]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C9A84C] text-[#0A0A0A] font-medium px-8 py-3 rounded-none hover:bg-[#E5C870] transition-colors text-sm uppercase tracking-widest disabled:opacity-50"
              data-testid="login-form-submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? t('login') : t('signup')}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#C9A84C]/20"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#1A1A1A] px-2 text-[#A3A3A3]">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="mt-6 w-full bg-transparent border border-[#C9A84C]/30 text-[#F5F2EB] font-medium px-8 py-3 rounded-none hover:bg-[#C9A84C]/10 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google Login
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-[#A3A3A3]">
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#C9A84C] hover:text-[#E5C870] font-medium"
            >
              {isLogin ? t('signup') : t('login')}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;